import { assert, isEmpty, isNullish } from "@bodil/core/assert";
import {
    attribute,
    attributeGetter,
    attributeSetter,
    Component,
    reactive,
    type Deps,
    type Emits,
} from "@bodil/dom/component";
import type { Signal } from "@bodil/signal";
import { mdiChevronUp } from "@mdi/js";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { map } from "lit/directives/map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";

import { BtkComboBoxItem } from "./combo-box-item";
import { RippleController } from "./controllers/ripple";
import { BtkDropdown } from "./dropdown";
import { BtkIcon } from "./icon";
import { BtkMenu } from "./menu";

import styles from "./combo-box.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-combo-box": BtkComboBox;
    }
}

@customElement("btk-combo-box")
export class BtkComboBox extends Component {
    emits!: Emits<"change">;

    static deps: Deps = [BtkDropdown, BtkIcon, BtkMenu];

    @attribute({ type: Boolean }) accessor disabled = false;

    @attributeGetter get value(): string {
        const item = this.items.get()[this.selectedIndex];
        return item === undefined ? "" : typeof item === "string" ? item : (item.value ?? "");
    }
    @attributeSetter set value(value: string) {
        for (let index = 0; index < this.items.get().length; index++) {
            const item = this.items.get()[index];
            if (item.value === value) {
                const changed = this.selectedIndex !== index;
                this.selectedIndex = index;
                if (changed) {
                    this.emit("change");
                }
                return;
            }
        }
    }

    private get items(): Signal.Computed<Array<BtkComboBoxItem>> {
        return this.querySlot({ reactive: true, flatten: true, instanceOf: BtkComboBoxItem });
    }

    @reactive selectedIndex = 0;

    private readonly combobox = createRef<HTMLDivElement>();
    private readonly dropdown = createRef<BtkDropdown>();
    private readonly menu = createRef<BtkMenu>();

    private readonly rippleController = new RippleController(this);

    private syncItems() {
        const previousValue = this.value;
        this.selectedIndex = 0;
        this.value = previousValue;
        this.requestUpdate();
    }

    private handleMenuOpen() {
        this.menu.value?.setCurrentIndex(this.selectedIndex);
        this.menu.value?.focus();
    }

    private handleMenuChanged(e: ToggleEvent) {
        this.combobox.value?.setAttribute("aria-expanded", e.newState);
    }

    private handleMouseDown(event: MouseEvent) {
        if (!this.disabled && this.combobox.value !== undefined) {
            this.rippleController.playRipple(this.combobox.value, event);
        }
    }

    private handleItemSelected(e: Event) {
        if (e.target instanceof BtkComboBoxItem) {
            e.stopPropagation();
            const index = this.items.get().indexOf(e.target);
            assert(index >= 0);
            this.selectedIndex = index;
            void this.dropdown.value?.hide();
            this.emit("change");
            this.combobox.value?.focus();
        }
    }

    protected override render(): unknown {
        if (this.items.get().length === 0) {
            return html`<slot style="display: none"></slot>`;
        }

        return html`
            <btk-dropdown
                ${ref(this.dropdown)}
                ?disabled=${this.disabled}
                placement="bottom"
                sync="width"
                @btk-popup-opened=${this.handleMenuOpen}
                @btk-popup-changed=${this.handleMenuChanged}
            >
                <div
                    ${ref(this.combobox)}
                    slot="trigger"
                    role="combobox"
                    tabindex="0"
                    aria-haspopup="menu"
                    aria-controls="combobox-menu"
                    class=${ifDefined(this.disabled ? "disabled" : undefined)}
                    @mousedown=${this.handleMouseDown}
                >
                    <ul class="selection">
                        ${map(
                            this.items.get(),
                            (item, index) => html`
                                <li
                                    class=${ifDefined(
                                        index === this.selectedIndex ? "active" : undefined,
                                    )}
                                    title=${ifDefined(item.title) as string}
                                >
                                    ${when(
                                        !isEmpty(item.icon) && isNullish(item.iconTemplate),
                                        () => html`
                                            <btk-icon size="large" name=${item.icon!}></btk-icon>
                                        `,
                                    )}
                                    ${when(!isNullish(item.iconTemplate), () => item.iconTemplate)}
                                    ${item.getContents()}
                                </li>
                            `,
                        )}
                    </ul>
                    <btk-icon class="chevron" size="large" path=${mdiChevronUp}></btk-icon>
                </div>

                <btk-menu
                    ${ref(this.menu)}
                    id="combobox-menu"
                    @btk-selected=${this.handleItemSelected}
                >
                    <slot></slot>
                </btk-menu>
            </btk-dropdown>
        `;
    }

    static styles = styles;
}
